/**
 * Add-ons & rules (T387 M5 read-only; M6 wires the Change flow): the three
 * priced add-on figures get the SAME change-with-a-reason grammar as every
 * other price — Change → edit strip → POST /api/meta-price — audited, live
 * without a restart. RULES are named rows with plain-words behaviour and
 * status chips: the return-trip rule carries "Owen to confirm" (Q30-gated —
 * there is deliberately NO functional confirm here; rules show who owns the
 * open question instead of an edit button).
 *
 * Q36 (soft, client-side): a change 10x off the current figure warns once;
 * a second Save proceeds; re-arms when the amount is edited.
 *
 * ⭐⭐ T1749 M9 (DS-26) — BOTH BLOCKS ARE `Table`s, AND THERE WAS NO `<table>`
 * HERE BEFORE. The screen shipped two `div.addon-card` containers of
 * `div.addon-row` flex rows: the markup said "boxes", the layout said "columns",
 * and the heads a reader needs — which figure is the price, what the grey chip in
 * the last column MEANS — existed nowhere. `Table` is the library's frame (§7.1),
 * so the columns are announced, `role=row` / `role=cell` replace the flex, and the
 * `.addon-card` / `.addon-row` rules retired with the markup in the same commit
 * (with `style-contract.spec.ts`'s `LIST_SURFACES` and `MONEY` entries re-pointed
 * beside them).
 *
 * ⛔ THE EDIT STRIP FITS THE 1:1 CELL MAPPING RATHER THAN SPANNING THE ROW.
 * `Table` maps cells to columns one for one and has no colspan outside its empty
 * state, so an open edit does NOT replace the row: the row's own cells change
 * what they hold — the description cell takes the "why", the price cell takes the
 * euro box, the action cell takes Save and Cancel. The `.edit-strip` box is not
 * worn here any more (it stays on `RateList.tsx`, its other consumer): a
 * row-spanning gold frame is exactly what the 1:1 mapping forbids, and three
 * per-cell frames would be three boxes where there was one.
 *
 * ⛔ THE TWO FIELDS CARRY VISIBLE LABELS NOW, AND THE STRIP IS A LABEL-LINE
 * TALLER FOR IT. Both were `aria-label`-only — a money box whose name only a
 * screen reader hears is the defect this design system exists to close
 * (`FormField`'s own rule: "a placeholder is not a label"). The label STRINGS are
 * unchanged, deliberately: `New price for <add-on>` and `Why is it changing?` are
 * what the green net and three unit arms already drive
 * (`e2e/tests/green/rates-book-addon-change.spec.ts:32`), so making them visible
 * costs no selector.
 *
 * ⛔ AND THE RULES CHIPS STAY RAW `<span class="chip chip--…">`. `Chip`'s tones
 * are `waiting | progress | ok | ended`; `goldline` and `quiet` have no tone and
 * minting one is a ruling against a zero-headroom ratchet, not a worker's edit.
 * Same decision the shelf covers took earlier in this milestone.
 */
import { useState } from 'preact/hooks';
import { FormField, Table } from '@ecmb/ui';
import { LATE_NIGHT_WINDOW } from '../../../shared/addons-schema.js';
import { setMetaPrice } from '../../lib/actions.js';
import { q36MagnitudeSlip } from '../../lib/q36.js';
import { euro } from '../../lib/format.js';
import { Banner } from '../../components/Banner.js';
import type { Registry } from '../../store/registry.js';

export type AddonsRulesProps = {
  registry: Registry;
  /** FRESH app_meta values (Admin re-reads them on every view entry) — never
   *  the login-time registry copy, which goes stale the moment a price is
   *  saved (T387 rework 1, repro A: €25 shown after a €30 save). */
  meta: Record<string, string>;
  onBack: () => void;
  /** Post-write invalidation: a saved price re-loads the whole admin section
   *  so every sibling view (shelf chips, lists, queue) sees the new figure. */
  onChanged?: () => void;
};

/** The three operator-priced add-ons: app_meta key + display copy. The label
 *  comes from the add-on registry where it exists (single source of truth);
 *  the desc is this screen's plain-words copy (design spec Frame 4). */
const PRICED_ROWS: { metaKey: string; engineKey: string; fallbackLabel: string; desc: string }[] = [
  {
    metaKey: 'meet_and_greet_eur', engineKey: 'meetAndGreet', fallbackLabel: 'Meet & greet',
    desc: 'Airport pickups. Driver waits in arrivals with a name board.',
  },
  {
    metaKey: 'extra_pickup_eur', engineKey: 'extraPickups', fallbackLabel: 'Extra pickups',
    desc: 'Each extra pickup on the way, per stop.',
  },
  {
    metaKey: 'trailer_eur', engineKey: 'trailer', fallbackLabel: 'Trailer',
    desc: 'Golf bags and luggage.',
  },
];

export function AddonsRules({ registry, meta, onBack, onChanged }: AddonsRulesProps) {
  // Instant post-save display: the override map shows the accepted price for
  // the ms the onChanged() re-read takes; fresh props.meta then carries it.
  const [savedCents, setSavedCents] = useState<Record<string, number>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editEuros, setEditEuros] = useState('');
  const [editNote, setEditNote] = useState('');
  const [amountWarned, setAmountWarned] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const addonLabel = (engineKey: string, fallback: string): string =>
    registry.descriptors.addons.find((a) => a.engineKey === engineKey)?.label ?? fallback;

  /** Current cents for a key: the session override, else login-time meta. */
  const centsOf = (metaKey: string): number | null => {
    if (savedCents[metaKey] != null) return savedCents[metaKey];
    const raw = meta[metaKey];
    const cents = raw == null ? Number.NaN : Math.round(Number(raw) * 100);
    return Number.isFinite(cents) ? cents : null;
  };

  const startEdit = (metaKey: string) => {
    const cents = centsOf(metaKey);
    setEditingKey(metaKey);
    setEditEuros(cents != null ? String(cents / 100) : '');
    setEditNote('');
    setAmountWarned(false);
    setNotice(null);
  };

  const commitEdit = async (metaKey: string) => {
    const euros = Number(editEuros);
    if (!Number.isFinite(euros) || euros <= 0) {
      setNotice({ kind: 'error', text: 'Enter a price in euro before saving.' });
      return;
    }
    if (!editNote.trim()) {
      setNotice({ kind: 'error', text: 'A reason note is required when changing a price.' });
      return;
    }
    const cents = Math.round(euros * 100);
    // Q36 warn-once: 10x off the current figure is almost always a typo.
    if (!amountWarned && q36MagnitudeSlip(cents, centsOf(metaKey))) {
      setAmountWarned(true);
      setNotice({ kind: 'error', text: 'That looks far from the current price. Check the amount, then save again to confirm.' });
      return;
    }
    const res = await setMetaPrice(metaKey, cents, editNote.trim());
    if (res.ok) {
      setSavedCents((s) => ({ ...s, [metaKey]: cents }));
      setEditingKey(null);
      setNotice({ kind: 'ok', text: 'Price saved. Quotes use it straight away.' });
      onChanged?.();
    } else {
      setNotice({ kind: 'error', text: res.message });
    }
  };

  const returnConfirmed = meta['return_rule_status'] === 'confirmed';

  return (
    <>
      <div class="book-list-head">
        <button type="button" class="btn btn--quiet" onClick={onBack}>All price lists</button>
        {/* ⭐ A2-pricelists-2 (T1723 M3): an `h1`, not an `h2`. Measured live at
            M2: `main h1` count on this sub-view was ZERO, so a screen-reader
            user landing here had no top-level heading and the outline began at
            level 2. `Admin.tsx` renders its own `h1` only on `view.kind ===
            'shelf'`, so this screen has no competing one; the same sweep
            promotes the account book, the rate list and the queue cover.
            ⛔ THE SWEEP IS SPLIT BY OWNER, and this is the half that had no
            milestone until the M3 pre-flight ruling. `Admin.tsx:441`'s promote
            form is M6's, and `PortalRequests.tsx:1216` is a DELIBERATE `h2`
            under a page `h1` and must NOT be swept. */}
        <h1 class="screen-title">Add-ons &amp; rules</h1>
      </div>

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {/* ⚠️ `.addon-block` IS A MEASURE, NOT A CARD. It carries the one thing
          `.addon-card` said that the frame law does not — `--measure-list`, the
          820px edge every kiosk admin list shares (`ADR4-D6`) — and nothing else:
          no fill, no border, no radius, no shadow, because `.data-table` paints
          all four now and a second frame around it would be two boxes. It takes
          `.addon-card`'s place in `style-contract.spec.ts`'s `LIST_SURFACES`. */}
      <div class="addon-block">
        <Table
          label="Priced add-ons"
          /* ⛔ FOUR HEADS, NONE BLANK, INCLUDING THE ACTION COLUMN (`Table`'s own
             rule): a `<th>` with no text is a column a screen reader cannot name.
             ⛔ AND NO `cellClass` IS PASSED, the same decision the Agreed prices
             tab recorded earlier in this milestone: `cellClass` may only name a
             class the SURFACE sheet declares, this screen declares none for a
             column, and an inert class is the defect this repo has found three
             times. The one thing the money column genuinely needs — a figure read
             by its last digit — is `align: 'end'`, which is choosing a variant
             rather than drawing one. */
          columns={[
            { head: 'Add-on' },
            { head: 'What it covers' },
            { head: 'Price', align: 'end' },
            { head: 'Change' },
          ]}
          rows={PRICED_ROWS.map((row, index) => {
            const cents = centsOf(row.metaKey);
            const label = addonLabel(row.engineKey, row.fallbackLabel);
            const editing = editingKey === row.metaKey;
            /* ⛔⛔ THE FIELD IDS ARE KEYED BY THE ROW INDEX, NEVER BY THE ADD-ON.
               A `FormField` derives its label's `for`, its hint id and its error
               id from the id it is given, so the id is written into the page's
               id space — and `Table.cellClass`'s own rule (nothing wire-derived
               reaches a rendered attribute) is the same rule one attribute over.
               The registry's `engineKey` arrives over `/api/service-descriptors`;
               the row ORDINAL is this file's. The per-screen duplicate-id arm in
               `admin-screens.spec.tsx` grades that the two ids on an open strip
               never collide. */
            const priceId = `addon-price-${String(index)}`;
            const whyId = `addon-why-${String(index)}`;
            return {
              key: row.metaKey,
              cells: [
                <span class="addon-name">{label}</span>,
                editing
                  ? (
                    <FormField
                      id={whyId}
                      label="Why is it changing?"
                      value={editNote}
                      onInput={setEditNote}
                      /* ⛔⛔ THE STRIP AUTOFOCUSES ITS FIRST FIELD, AND UNTIL M9
                         rework 1 IT FOCUSED ITS SECOND (impeccable K). The 1:1
                         cell mapping puts the cells in column order — why, then
                         price — but the `elementRef` was still on the euro box,
                         so a measured tab walk from the opened state ran
                         `addon-price-0 → Save → Cancel → the next row's Change`
                         and `Why is it changing?` was reachable by Shift+Tab
                         only. Before M9 the strip was `[euro, why]` and forward
                         Tab reached the note, so this is a regression of this
                         milestone's own re-cut.
                         ⇒ the focus moves to the first cell rather than the
                         cells swapping: swapping them would put a money figure
                         under the `What it covers` head and a sentence under
                         `Price`, inverting the two heads M9 just added, and the
                         heads are the whole reason this screen became a table. */
                      elementRef={(el) => {
                        if (el && !el.dataset['autofocused']) {
                          el.dataset['autofocused'] = '1';
                          el.focus();
                        }
                      }}
                    />
                  )
                  : <span class="addon-desc">{row.desc}</span>,
                editing
                  ? (
                    <FormField
                      id={priceId}
                      label={`New price for ${label}`}
                      type="number"
                      inputMode="decimal"
                      value={editEuros}
                      onInput={(value) => { setEditEuros(value); setAmountWarned(false); }}
                    />
                  )
                  : <span class="addon-value">{cents != null ? euro(cents) : 'Not set'}</span>,
                editing
                  ? (
                    /* ⛔ A WRAPPER, BECAUSE A `<td>` CANNOT CARRY THE GAP (M9
                       rework 1, impeccable L). Measured with the strip open:
                       Save y193 h44, Cancel y237 — 0px between two 44px targets
                       stacked in a 118px column, which is a mis-tap on a docked
                       iPad. `display: flex` on the cell itself would take it out
                       of the table's box model, so the pair gets its own box. */
                    <div class="addon-edit-actions">
                      <button type="button" class="btn btn--secondary btn--inline" onClick={() => void commitEdit(row.metaKey)}>Save</button>
                      <button type="button" class="btn btn--quiet btn--inline" onClick={() => setEditingKey(null)}>Cancel</button>
                    </div>
                  )
                  : (
                    <button
                      type="button" class="btn btn--quiet btn--inline"
                      aria-label={`Change ${label} price`}
                      aria-expanded={false}
                      onClick={() => startEdit(row.metaKey)}
                    >
                      Change
                    </button>
                  ),
              ],
            };
          })}
        />
      </div>

      <div class="addon-block">
        {/* ⚠️ `Where it stands`, NOT `Who owns it`, and the difference is honesty.
            Two of the four values in this column — `No set price`, `No charge` —
            name a STATE rather than a person, so an owner head would promise
            something the column does not hold on two of its three rows. A column
            head is a promise about every cell under it. */}
        <Table
          label="Pricing rules"
          columns={[
            { head: 'Rule' },
            { head: 'What it says' },
            { head: 'Where it stands' },
          ]}
          rows={[
            {
              key: 'return-trip',
              cells: [
                <span class="addon-name">Return trip</span>,
                <span class="addon-desc">Doubles everything except meet and greet.</span>,
                returnConfirmed
                  ? <span class="chip chip--ok">Confirmed</span>
                  : <span class="chip chip--goldline">Owen to confirm</span>,
              ],
            },
            {
              key: 'late-night',
              cells: [
                <span class="addon-name">Late night</span>,
                <span class="addon-desc">
                  Trips between {LATE_NIGHT_WINDOW.start} and {LATE_NIGHT_WINDOW.end}. No set charge, Helen prices each trip.
                </span>,
                <span class="chip chip--quiet">No set price</span>,
              ],
            },
            {
              key: 'accessible-vehicle',
              cells: [
                <span class="addon-name">Accessible vehicle</span>,
                <span class="addon-desc">Wheelchair or bariatric vehicle. Same price, right vehicle.</span>,
                <span class="chip chip--quiet">No charge</span>,
              ],
            },
          ]}
        />
      </div>
    </>
  );
}
